import { Location } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WindowService } from '@progress/kendo-angular-dialog';
import { WindowRef } from '@progress/kendo-angular-dialog/window/models/window-ref';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  GkKendoWindowActionButtons,
  GkKendoWindowSettings,
  KendoWindowInstanceName,
} from '../kendo-window.model';

@Injectable()
export class GkKendoWindowService {
  private renderer: Renderer2;
  private windowInstancesMap = new Map<string, WindowRef>();
  private windowRelationshipsMap = new Map<string, Set<string>>();

  constructor(
    private deviceService: DeviceDetectorService,
    private translateService: TranslateService,
    private rendererFactory: RendererFactory2,
    private windowService: WindowService,
    @Inject('window') public window: Window,
    private location: Location,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    setTimeout(() => {
      this.handleClosingLastOpenedWindowWhenBackButtonPressedOnMobileDevice();
    }, 0);

    // Add scroll event listener to keep windows within viewport
    this.setupScrollHandler();
  }

  private handleClosingLastOpenedWindowWhenBackButtonPressedOnMobileDevice(): void {
    if (this.deviceService.isMobile()) {
      this.location.subscribe(() => {
        const lastItemInWindowInstancesMap = [...this.windowInstancesMap].pop();
        if (lastItemInWindowInstancesMap) {
          const windowRef = lastItemInWindowInstancesMap[1];
          if (windowRef) {
            windowRef.close();
          }
        }
      });
    }
  }

  open(gkKendoWindowSettings: GkKendoWindowSettings): WindowRef {
    history.pushState(
      {
        windowInstanceName: gkKendoWindowSettings.windowInstanceName,
      },
      '',
      window.location.href,
    );

    let windowReference: WindowRef;
    if (
      !this.windowInstancesMap.has(gkKendoWindowSettings.windowInstanceName)
    ) {
      windowReference = this.windowService.open({
        title: gkKendoWindowSettings.title
          ? this.translateService.instant(gkKendoWindowSettings.title)
          : '',
        content: gkKendoWindowSettings.content,
        height: gkKendoWindowSettings.height,
        width: gkKendoWindowSettings.width,
        left: gkKendoWindowSettings.left,
        resizable:
          gkKendoWindowSettings.resizable ?? !this.deviceService.isMobile(),
        state: this.deviceService.isMobile()
          ? 'maximized'
          : gkKendoWindowSettings.state || 'default',
        cssClass: `${gkKendoWindowSettings.cssClass} mh-100`,
        htmlAttributes: {
          windowInstanceName: gkKendoWindowSettings.windowInstanceName,
        },
      });

      if (gkKendoWindowSettings.modal) {
        this.createOverlay(windowReference);
      }
      if (windowReference.content) {
        windowReference.content.instance.parent = gkKendoWindowSettings.parent;
        windowReference.content.instance.windowRef = windowReference;
      }
      // if (gkKendoWindowSettings.windowFocus === false) {
      //   this.windowReference.window.instance.onComponentFocus = (): void =>
      //     undefined;
      // }

      // this.windowReference.window.instance.onComponentFocus = (): void => {
      //   this.windowReference.window.instance['renderer'].addClass(
      //     this.windowReference.window.instance['el'].nativeElement,
      //     'k-focus',
      //   );
      // };
      windowReference.window.instance.dragEnd.subscribe(() => {
        this.onWindowDragEnd(windowReference);
      });

      this.windowInstancesMap.set(
        gkKendoWindowSettings.windowInstanceName,
        windowReference,
      );
      windowReference.result.subscribe(() => {
        this.closeChildWindows(gkKendoWindowSettings.windowInstanceName);

        this.windowInstancesMap.delete(
          gkKendoWindowSettings.windowInstanceName,
        );
        if (gkKendoWindowSettings.modal) {
          this.removeOverlay(gkKendoWindowSettings.windowInstanceName);
        }
      });

      windowReference.window.instance.themeColor = 'primary';
      if (this.deviceService.isMobile()) {
        this.hideKendoWindowActions(windowReference, [
          GkKendoWindowActionButtons.restore,
        ]);
      }
      if (gkKendoWindowSettings.hiddenActionButtons?.length) {
        this.hideKendoWindowActions(
          windowReference,
          gkKendoWindowSettings.hiddenActionButtons,
        );
      }
    }

    if (gkKendoWindowSettings.parent) {
      this.trackWindowRelationship(
        gkKendoWindowSettings.parentWindowInstanceName,
        gkKendoWindowSettings.windowInstanceName,
      );
    }

    return windowReference;
  }

  private trackWindowRelationship(parentName: string, childName: string): void {
    if (!this.windowRelationshipsMap.has(parentName)) {
      this.windowRelationshipsMap.set(parentName, new Set());
    }
    this.windowRelationshipsMap.get(parentName).add(childName);
  }

  private createOverlay(windowReference: WindowRef): void {
    setTimeout(() => {
      const windowElement = windowReference.window.location.nativeElement;
      const windowZIndex = parseInt(windowElement.style.zIndex, 10) || 1000;
      const overlayElement = this.renderer.createElement('div');
      const windowInstanceName =
        windowReference.window.instance.htmlAttributes['windowInstanceName'];
      const overlayClass = `k-overlay-${windowInstanceName}`;
      this.renderer.addClass(overlayElement, 'k-overlay');
      this.renderer.addClass(overlayElement, overlayClass);
      this.renderer.setStyle(overlayElement, 'zIndex', windowZIndex - 1);
      this.renderer.appendChild(this.window.document.body, overlayElement);
    }, 0);
  }

  private removeOverlay(windowInstanceName: string): void {
    const overlayClass = `k-overlay-${windowInstanceName}`;
    const overlayElement = this.window.document.querySelector(
      `.${overlayClass}`,
    );
    if (overlayElement) {
      this.renderer.removeChild(this.window.document.body, overlayElement);
    }
  }

  public hideKendoWindowActions(
    gkWindowRef: WindowRef,
    windowActions: GkKendoWindowActionButtons[],
  ): void {
    setTimeout(() => {
      windowActions.forEach((windowAction) => {
        const actionButton =
          gkWindowRef.window.location.nativeElement.querySelector(
            `button[${windowAction}]`,
          );
        if (actionButton) {
          this.renderer.addClass(actionButton, 'd-none');
        }
      });
    }, 1);
  }

  getWindowInstanceByName(
    name: KendoWindowInstanceName,
  ): WindowRef | undefined {
    return this.windowInstancesMap.get(name);
  }

  private setupScrollHandler(): void {
    // Use passive listener for better performance
    this.window.addEventListener(
      'scroll',
      () => {
        this.constrainAllWindowsToViewport();
      },
      { passive: true },
    );
  }

  private constrainAllWindowsToViewport(): void {
    // Apply viewport constraints to all open windows
    this.windowInstancesMap.forEach((windowReference) => {
      this.constrainWindowToViewport(windowReference);
    });
  }

  private constrainWindowToViewport(windowReference: WindowRef): void {
    const windowInstance = windowReference.window.instance;
    const offset = 0;

    // Get the actual window element dimensions
    const windowElement = windowReference.window.instance['el'].nativeElement;
    const windowRect = windowElement.getBoundingClientRect();
    const windowWidth = windowRect.width;
    const windowHeight = windowRect.height;

    // Get current viewport dimensions (visible area)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Get current scroll position
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate the window's position relative to the viewport
    const windowTopRelativeToViewport = windowInstance.top - scrollY;
    const windowLeftRelativeToViewport = windowInstance.left - scrollX;

    // Calculate maximum allowed positions within the current viewport
    const maxTopInViewport = viewportHeight - windowHeight - offset;
    const maxLeftInViewport = viewportWidth - windowWidth - offset;

    // Check if window needs repositioning
    let needsRepositioning = false;
    let constrainedTop = windowTopRelativeToViewport;
    let constrainedLeft = windowLeftRelativeToViewport;

    if (constrainedTop < offset) {
      constrainedTop = offset;
      needsRepositioning = true;
    }

    if (constrainedTop > maxTopInViewport) {
      constrainedTop = maxTopInViewport;
      needsRepositioning = true;
    }

    if (constrainedLeft < offset) {
      constrainedLeft = offset;
      needsRepositioning = true;
    }

    if (constrainedLeft > maxLeftInViewport) {
      constrainedLeft = maxLeftInViewport;
      needsRepositioning = true;
    }

    // Only reposition if necessary to avoid unnecessary updates
    if (needsRepositioning) {
      // Convert back to absolute page coordinates
      const absoluteTop = constrainedTop + scrollY;
      const absoluteLeft = constrainedLeft + scrollX;

      // Set the window position
      windowInstance.setOffset('top', absoluteTop);
      windowInstance.setOffset('left', absoluteLeft);
    }
  }

  private onWindowDragEnd(windowReference: WindowRef): void {
    // Use the same constraint logic as scroll handling
    this.constrainWindowToViewport(windowReference);
  }

  private closeChildWindows(parentWindowName: string): void {
    const childWindows = this.windowRelationshipsMap.get(parentWindowName);
    if (!childWindows) {
      return;
    }

    for (const childName of childWindows) {
      const childWindow = this.windowInstancesMap.get(childName);
      if (childWindow) {
        this.closeChildWindows(childName);

        childWindow.close();
      }
    }

    this.windowRelationshipsMap.delete(parentWindowName);
  }
}
